import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { RadioGroup } from "@arcadeai/design-system/components/ui/atoms/radio-group";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { Globe, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { PROJECT } from "@/app/capabilities/permissions";
import { useSession } from "@/lib/auth-client";
import {
  useGetSessionVerification,
  useUpdateSessionVerification,
} from "../api/hooks";
import { type VerificationFormValues, verificationFormSchema } from "../types";
import { CustomVerifierTester } from "./custom-verifier-tester";
import {
  NoVerifierWarning,
  VerificationRadioItem,
} from "./verification-radio-item";

export function SettingsForm() {
  const { data } = useGetSessionVerification();
  const updateMutation = useUpdateSessionVerification();
  const { data: session } = useSession();
  const { canProject } = useProjectPermissions();
  // Without security_settings_update the form is fully read-only: a disabled
  // fieldset locks every control (radio options, URL, tester, Save) at once, and
  // onSubmit is a no-op as a backstop.
  const canUpdate = canProject(PROJECT.securitySettings.update);

  const formValues = useMemo((): VerificationFormValues => {
    if (data.unsafe_skip_verification) {
      return { verifierType: "no-verifier", customVerifierUrl: "" };
    }
    if (data.verifier_url) {
      return {
        verifierType: "custom-verifier",
        customVerifierUrl: data.verifier_url,
      };
    }
    return { verifierType: "arcade-session", customVerifierUrl: "" };
  }, [data.unsafe_skip_verification, data.verifier_url]);

  const form = useForm<VerificationFormValues>({
    resolver: zodResolver(verificationFormSchema),
    values: formValues,
    mode: "onChange",
  });

  const verifierType = form.watch("verifierType");
  const customVerifierUrl = form.watch("customVerifierUrl");
  const showNoVerifierOption =
    data.unsafe_skip_verification_choice_allowed !== false;

  const onSubmit = (values: VerificationFormValues) => {
    if (!canUpdate) {
      return;
    }
    const settings = {
      unsafe_skip_verification: values.verifierType === "no-verifier",
      verifier_url:
        values.verifierType === "custom-verifier"
          ? values.customVerifierUrl?.trim() || null
          : null,
    };

    updateMutation.mutate(settings);
  };

  const { isValid } = form.formState;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset
          className="min-w-0 space-y-6 border-0 p-0"
          disabled={!canUpdate}
        >
          <FormField
            control={form.control}
            name="verifierType"
            render={({ field }) => (
              <FormItem className="space-y-4">
                <FormControl>
                  <RadioGroup
                    className="space-y-2"
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    {!!showNoVerifierOption && (
                      <VerificationRadioItem value="no-verifier">
                        <NoVerifierWarning />
                      </VerificationRadioItem>
                    )}

                    <VerificationRadioItem value="arcade-session" />

                    <VerificationRadioItem value="custom-verifier" />
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {verifierType === "custom-verifier" && (
            <div className="space-y-6 px-4">
              <Separator />
              <FormField
                control={form.control}
                name="customVerifierUrl"
                render={({ field }) => (
                  <FormItem className="mt-5 space-y-4">
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-brand-text" />
                      <FormLabel className="font-semibold text-lg">
                        Custom verifier URL
                      </FormLabel>
                      <span className="font-medium text-destructive text-sm">
                        *
                      </span>
                    </div>
                    <FormDescription className="text-muted-foreground">
                      Enter the URL where users will be redirected to verify
                      their identity in your app. Learn about{" "}
                      <a
                        className="font-medium text-link hover:underline"
                        href="https://docs.arcade.dev/home/auth/secure-auth-production#build-a-custom-user-verifier"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        building a verification route
                      </a>{" "}
                      in our docs.
                    </FormDescription>
                    <FormControl>
                      <Input
                        className="max-w-lg text-base"
                        placeholder="https://your-domain.com/verify"
                        required
                        type="url"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <CustomVerifierTester
                defaultUserId={
                  session?.user?.rawEmail ?? session?.user?.email ?? ""
                }
                verifierUrl={customVerifierUrl}
              />
            </div>
          )}

          <Separator />

          <div className="flex items-center justify-between pt-2">
            <p className="text-muted-foreground text-sm">
              Changes will take effect immediately after saving.
            </p>
            <Button
              className="px-8"
              disabled={updateMutation.isPending || !isValid}
              size="lg"
              type="submit"
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save settings"
              )}
            </Button>
          </div>
        </fieldset>
      </form>
    </Form>
  );
}
